/**
 * @copyright
 * Copyright (c) 2024 Accretive Technology Group, Inc. All rights
 * reserved. For use only by Accretive Technology, its employees
 * and contractors. DO NOT DISTRIBUTE.
 */
import axios, { isAxiosError } from "axios";
import { AxiosInstance } from "axios/index";
import { Store } from "redux";

import { AuthActions } from "~/features/auth/slice";
import { logoutAsync } from "~/features/auth/thunks";
import { AppDispatch, RootState } from "~/state/types";
import reportError from "~/utils/reportError";

const ApiCaller = axios.create({
  baseURL: "/api",
  withCredentials: false,
});

export const initApiCallerInterceptors = (instance: AxiosInstance, store: Store<RootState>) => {
  instance.interceptors.response.use(
    (res) => res,
    (error: Error) => {
      if (!isAxiosError(error)) {
        return Promise.reject(error);
      }

      const loggedIn = !!store.getState().auth.account;
      const viewOnly = store.getState().auth.viewOnly;
      const unauthorized = error.response?.status === 401;
      if (loggedIn && unauthorized && !viewOnly) {
        (store.dispatch as AppDispatch)(logoutAsync()).catch(reportError);
      }

      return Promise.reject(error);
    },
  );
  instance.interceptors.response.use(
    (res) => res,
    (error: Error) => {
      if (!isAxiosError(error)) {
        return Promise.reject(error);
      }

      const loggedIn = !!store.getState().auth.account;
      const viewOnly = store.getState().auth.viewOnly;
      const forbidden = error.response?.status === 403;
      const need2FA = error.response?.data === "2FA required.";

      if (loggedIn && forbidden && need2FA && !viewOnly) {
        (store.dispatch as AppDispatch)(AuthActions.setNeed2FA());
      }

      return Promise.reject(error);
    },
  );
  // TODO: refactor error handling for all endpoints
  instance.interceptors.response.use(
    (res) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      const error = res.data?.error as string;

      if (error) {
        throw new Error(error);
      }
      return res;
    },
    (error: Error) => {
      return Promise.reject(error);
    },
  );
};

export default ApiCaller;
